import { Box, Chip, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import type {
  TipoInstalacion,
  TipoInstalacionSeleccion,
} from "./useTiposInstalacion";
import { etiquetaTipoInstalacion, tipoInstalacionPorDefecto } from "./useTiposInstalacion";
import CampoFotoEvidencia from "../evidencias/CampoFotoEvidencia";
import type { FotoEvidencia } from "../evidencias/CampoFotoEvidencia";

/**
 * Selector en CASCADA de "Tipo de instalación": un dropdown de tipo y, solo si el
 * tipo elegido tiene subtipos (p.ej. Empaquetado), un segundo dropdown de subtipo
 * (Tipo 1/2/3 por rango de metros). El subtipo es obligatorio cuando el tipo lo
 * tiene. Componente CONTROLADO (editor + readOnly).
 *
 * Se persiste en `agendamiento.observaciones.tipo_instalacion`.
 */

const ACCENT = "#00695c";

type Props = {
  value?: TipoInstalacionSeleccion | null;
  onChange?: (value: TipoInstalacionSeleccion) => void;
  readOnly?: boolean;
  /**
   * Bloquea SOLO los selectores de tipo/subtipo (no la foto). Se usa en la Orden
   * de Trabajo del TÉCNICO: el tipo de instalación lo definen Operaciones y
   * Activaciones; el técnico solo puede ver el tipo y adjuntar la evidencia.
   */
  selectionReadOnly?: boolean;
  tipos: TipoInstalacion[];
  /** Sube la foto del tipo (solo técnico); si viene, habilita el campo de foto. */
  onUploadFoto?: (file: File) => Promise<FotoEvidencia>;
  title?: string;
};

/** ¿La selección tiene un subtipo obligatorio pendiente? (para validaciones). */
export function tipoInstalacionSubtipoPendiente(
  sel: TipoInstalacionSeleccion | null | undefined,
  tipos: TipoInstalacion[]
): boolean {
  if (!sel || !sel.tipo) return false;
  const t = tipos.find((x) => x.nombre === sel.tipo);
  const tieneSub = !!(t?.subtipos && t.subtipos.length > 0);
  return tieneSub && !sel.subtipo;
}

export default function TipoInstalacionSelector({
  value,
  onChange,
  readOnly = false,
  selectionReadOnly = false,
  tipos,
  onUploadFoto,
  title = "Tipo de instalación",
}: Props) {
  const sel: TipoInstalacionSeleccion =
    value && value.tipo ? value : tipoInstalacionPorDefecto(tipos);

  const tipoActual = tipos.find((t) => t.nombre === sel.tipo);
  const subtipos = tipoActual?.subtipos || [];
  const requiereSubtipo = subtipos.length > 0;

  const emit = (next: TipoInstalacionSeleccion) => onChange && onChange(next);

  const cambiarTipo = (nombreTipo: string) => {
    const t = tipos.find((x) => x.nombre === nombreTipo);
    if (t?.subtipos && t.subtipos.length > 0) {
      // Al elegir un tipo con subtipos, se limpia el subtipo (obligatorio elegir).
      emit({ tipo: nombreTipo });
    } else {
      emit({ tipo: nombreTipo });
    }
  };

  const cambiarSubtipo = (nombreSub: string) => {
    const s = subtipos.find((x) => x.nombre === nombreSub);
    emit({ tipo: sel.tipo, subtipo: nombreSub, rango: s?.rango });
  };

  // ---------------- READ-ONLY ----------------
  if (readOnly) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AccountTreeOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip
            label={etiquetaTipoInstalacion(sel)}
            sx={{ fontWeight: 700, bgcolor: "rgba(0,105,92,0.1)", color: ACCENT }}
          />
          {sel.foto?.url && (
            <CampoFotoEvidencia label="Evidencia del tipo de instalación" value={sel.foto as any} readOnly />
          )}
        </Stack>
      </Box>
    );
  }

  // ---------------- EDITOR ----------------
  const subtipoPendiente = requiereSubtipo && !sel.subtipo;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <AccountTreeOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      {selectionReadOnly && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          El tipo de instalación lo definen Operaciones/Activaciones. Aquí solo puedes ver el tipo y adjuntar la evidencia.
        </Typography>
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Tipo de instalación"
            value={sel.tipo || ""}
            onChange={(e) => cambiarTipo(e.target.value)}
            disabled={selectionReadOnly}
          >
            {tipos.length === 0 && (
              <MenuItem value="" disabled>
                Sin tipos en el catálogo
              </MenuItem>
            )}
            {tipos.map((t) => (
              <MenuItem key={t.nombre} value={t.nombre}>
                {t.nombre}
                {t.subtipos && t.subtipos.length > 0 ? " …" : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {requiereSubtipo && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo (rango de metros)"
              value={sel.subtipo || ""}
              onChange={(e) => cambiarSubtipo(e.target.value)}
              disabled={selectionReadOnly}
              error={subtipoPendiente}
              helperText={subtipoPendiente ? "Requerido para Empaquetado" : ""}
            >
              {subtipos.map((s) => (
                <MenuItem key={s.nombre} value={s.nombre}>
                  {s.nombre}
                  {s.rango ? ` (${s.rango})` : ""}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>
      {/* Evidencia fotográfica del tipo de instalación. La sube el técnico
          (onUploadFoto); en Activaciones/Operaciones no se sube pero SÍ debe
          verse la foto que ya subió el técnico, por eso también se muestra en
          modo solo-lectura cuando existe. */}
      {(onUploadFoto || sel.foto?.url) && (
        <Box sx={{ mt: 1.25 }}>
          <CampoFotoEvidencia
            label="Evidencia del tipo de instalación"
            value={sel.foto as any}
            onUpload={onUploadFoto}
            onChange={onUploadFoto ? (f) => emit({ ...sel, foto: f }) : undefined}
            readOnly={!onUploadFoto}
          />
        </Box>
      )}
    </Box>
  );
}
